/**
 * Category Operations
 *
 * Handles all category management operations
 */

import { query, queryOne, execute, transaction, saveDB } from '../../db';
import { PROTECTED_CATEGORIES } from '../../constants';
import { setCategoryAlias } from './categoryAliasOperations';

/**
 * Get all categories
 */
export async function getAll(): Promise<string[]> {
  try {
    const rows = query<{ name: string }>('SELECT name FROM categories ORDER BY name');
    return rows.map((row) => row.name);
  } catch (error) {
    console.error('[categoryOperations] Error loading categories:', error);
    return [];
  }
}

/**
 * Add a new category
 */
export async function add(category: string): Promise<{ success: boolean; category?: string; categories?: string[]; error?: string }> {
  try {
    execute('INSERT OR IGNORE INTO categories (name) VALUES (?)', [category]);
    await saveDB();

    const categories = await getAll();
    return { success: true, category, categories };
  } catch (error) {
    console.error('[categoryOperations] Error adding category:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Update/rename a category (cascades to transactions and rules)
 */
export async function update(
  oldName: string,
  newName: string
): Promise<{ success: boolean; categories?: string[]; updated?: { transactions: number; rules: number }; error?: string }> {
  try {
    let transactionCount = 0;
    let ruleCount = 0;

    transaction(() => {
      // Update category name
      execute('UPDATE categories SET name = ? WHERE name = ?', [newName, oldName]);

      // Update transactions
      const txnResult = query<{ count: number }>('SELECT COUNT(*) as count FROM transactions WHERE category = ?', [oldName]);
      transactionCount = txnResult[0]?.count || 0;
      execute('UPDATE transactions SET category = ? WHERE category = ?', [newName, oldName]);

      // Update rules
      const ruleResult = query<{ count: number }>('SELECT COUNT(*) as count FROM rules WHERE category = ?', [oldName]);
      ruleCount = ruleResult[0]?.count || 0;
      execute('UPDATE rules SET category = ? WHERE category = ?', [newName, oldName]);

      // Create alias so HybridCategorizer's hardcoded rules still work
      // e.g., when renaming "Groceries" → "Food Shopping", create alias "Groceries" → "Food Shopping"
      execute('INSERT OR REPLACE INTO category_aliases (old_name, new_name) VALUES (?, ?)', [oldName, newName]);
    });

    await saveDB();

    const categories = await getAll();
    return {
      success: true,
      categories,
      updated: { transactions: transactionCount, rules: ruleCount }
    };
  } catch (error) {
    console.error('[categoryOperations] Error updating category:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Delete a category (update transactions and rules to replacement category)
 */
export async function deleteCategory(
  name: string,
  replacementCategory?: string
): Promise<{
  success: boolean;
  categories?: string[];
  updated?: { transactions: number; rules: number };
  replacementCategory?: string;
  error?: string;
}> {
  try {
    // Prevent deletion of protected categories
    if (PROTECTED_CATEGORIES.includes(name)) {
      return {
        success: false,
        error: `Cannot delete "${name}" - this is a system-critical category used by auto-categorization rules.`
      };
    }

    let transactionCount = 0;
    let ruleCount = 0;
    const replacement = replacementCategory || 'Uncategorized';

    transaction(() => {
      // Update transactions
      const txnResult = query<{ count: number }>('SELECT COUNT(*) as count FROM transactions WHERE category = ?', [name]);
      transactionCount = txnResult[0]?.count || 0;
      execute('UPDATE transactions SET category = ? WHERE category = ?', [replacement, name]);

      // Update rules
      const ruleResult = query<{ count: number }>('SELECT COUNT(*) as count FROM rules WHERE category = ?', [name]);
      ruleCount = ruleResult[0]?.count || 0;
      execute('UPDATE rules SET category = ? WHERE category = ?', [replacement, name]);

      // Create alias so HybridCategorizer's hardcoded rules still work
      // e.g., when deleting "Groceries" and replacing with "Food Shopping", create alias "Groceries" → "Food Shopping"
      execute('INSERT OR REPLACE INTO category_aliases (old_name, new_name) VALUES (?, ?)', [name, replacement]);

      // Delete category
      execute('DELETE FROM categories WHERE name = ?', [name]);
    });

    await saveDB();

    const categories = await getAll();
    return {
      success: true,
      categories,
      updated: { transactions: transactionCount, rules: ruleCount },
      replacementCategory: replacement
    };
  } catch (error) {
    console.error('[categoryOperations] Error deleting category:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
